import { isFuture, parse as parseDate } from 'date-fns';

import { BRANCH_TYPE, EIV_STATUS, MTN_OTHER_DOCUMENT_ID, TENANT_ID } from 'global/constants';

import { canReseal, parseMeterInstallation } from './DataUtils';
import { parseEIVDateStringToEfixDateString } from './DatesUtils';
import { getEntryType } from './InstallerUtils';
import { GRIDNET_PROVIDER, getTenantId, isGridnetProviderOfCurrentTenant } from './tenantUtil';

export const fetchFile = async (path) =>
  new Promise((resolve, reject) => {
    fetch(path)
      .then((response) => {
        if (!response.ok) {
          reject(new Error('Network response was not ok'));
        }
        return response.blob();
      })
      .then((fileBlob) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve(e.target.result);
        };
        reader.readAsDataURL(fileBlob);
      })
      .catch((error) => {
        reject(new Error('There has been a problem with your fetch operation:', error));
      });
  });

export const fetchFont = async (path) =>
  new Promise((resolve, reject) => {
    fetch(path)
      .then((response) => {
        if (!response.ok) {
          reject(new Error('Network response was not ok'));
        }
        response.arrayBuffer().then((arrayBuffer) => {
          resolve(arrayBuffer);
        });
      })
      .catch((error) => {
        reject(new Error('There has been a problem with your fetch operation:', error));
      });
  });

export const isDocumentDownloadElegible = (documentInformation, parsedData, selectedExpert) => {
  const { type, id: documentId, guest } = documentInformation;
  const {
    isEnergyActive: isEnergyActiveInstaller,
    isGasActive: isGasActiveInstaller,
    gridProviderEnergy,
    gridProviderGas,
    pflegendeBu,
  } = parsedData;

  const {
    isEnergyActive: isEnergyActiveExpert,
    energyValidityDate,
    isGasActive: isGasActiveExpert,
    gasValidityDate,
  } = selectedExpert;

  const isActiveAndValid = (isActiveInstaller, isActiveExpert, validityDate) =>
    isActiveInstaller &&
    isActiveExpert &&
    isFuture(parseDate(validityDate, 'dd.MM.yyyy', new Date()));

  const isGuestTypeEligible = (
    branchType,
    entryType,
    isActiveInstaller,
    isActiveExpert,
    validityDate
  ) =>
    type === branchType &&
    getEntryType(entryType) === 'guest' &&
    guest &&
    isActiveAndValid(isActiveInstaller, isActiveExpert, validityDate);

  const isMainTypeEligible = (
    branchType,
    entryType,
    isActiveInstaller,
    isActiveExpert,
    validityDate,
    gridProvider
  ) => {
    if (
      type === branchType &&
      getEntryType(entryType) === 'main' &&
      !guest &&
      isActiveAndValid(isActiveInstaller, isActiveExpert, validityDate)
    ) {
      if ([TENANT_ID.SHNG, TENANT_ID.AVA, TENANT_ID.BAG].includes(getTenantId())) {
        if (isGridnetProviderOfCurrentTenant(gridProvider.id)) {
          return gridProvider.id === documentId;
        }
        const mappedGridnetProvider = GRIDNET_PROVIDER.SHNG[pflegendeBu];
        return documentId === (mappedGridnetProvider || GRIDNET_PROVIDER.SHNG.SHNG);
      }

      if (TENANT_ID.MTN === getTenantId()) {
        if (isGridnetProviderOfCurrentTenant(gridProvider.id)) {
          return gridProvider.id === documentId;
        }

        return type === BRANCH_TYPE.ENERGY && documentId === MTN_OTHER_DOCUMENT_ID;
      }

      return true;
    }
    return false;
  };

  return (
    isGuestTypeEligible(
      BRANCH_TYPE.ENERGY,
      parsedData.entryTypeEnergy,
      isEnergyActiveInstaller,
      isEnergyActiveExpert,
      energyValidityDate
    ) ||
    isGuestTypeEligible(
      BRANCH_TYPE.GAS,
      parsedData.entryTypeGas,
      isGasActiveInstaller,
      isGasActiveExpert,
      gasValidityDate
    ) ||
    isMainTypeEligible(
      BRANCH_TYPE.ENERGY,
      parsedData.entryTypeEnergy,
      isEnergyActiveInstaller,
      isEnergyActiveExpert,
      energyValidityDate,
      gridProviderEnergy
    ) ||
    isMainTypeEligible(
      BRANCH_TYPE.GAS,
      parsedData.entryTypeGas,
      isGasActiveInstaller,
      isGasActiveExpert,
      gasValidityDate,
      gridProviderGas
    )
  );
};

export const prepareDocumentDownloadData = (installer, experts, contracts) => {
  const data = {
    companyName1: installer.firmenname1,
    companyName2: installer.firmenname2 || '',
    companyName3: installer.firmenname3 || '',
    companyId: `${installer.id}`,
    companyStreet: installer.bueroStrasse,
    companyHouseNumber: installer.bueroHausnummer,
    companyPostal: installer.bueroPlz,
    referenceNumberEnergy: installer.referenznummerStrom || '',
    companyCity: installer.bueroOrt,
    companyTypeEnergy: installer.bBetriebsartStrom?.name || '',
    companyTypeGas: installer.bBetriebsartGas?.name || '',
    meterInstallationGas: parseMeterInstallation(installer.zaehlermontageGas),
    meterInstallationEnergy: parseMeterInstallation(installer.zaehlermontageStrom),
    gridProviderEnergy: installer.bNetzbetreiberStrom,
    gridProviderGas: installer.bNetzbetreiberGas,
    entryTypeEnergy: installer.eintragungsartStrom,
    entryTypeGas: installer.eintragungsartGas,
    pflegendeBu: installer.pflegendeBu,
    limitationGas: installer.eintragungsartErgGas || '',
    limitationEnergy: installer.eintragungsartErgStrom || '',
    isEnergyActive: installer.statusStrom === EIV_STATUS.ACTIVE,
    isGasActive: installer.statusGas === EIV_STATUS.ACTIVE,
    canReseal: canReseal(contracts),
    installers: experts
      .filter((expert) => {
        const isValidEnergyStatus =
          expert.statusStrom === EIV_STATUS.ACTIVE || expert.statusStrom === EIV_STATUS.INACTIVE;
        const isValidGasStatus =
          expert.statusGas === EIV_STATUS.ACTIVE || expert.statusGas === EIV_STATUS.INACTIVE;

        return isValidEnergyStatus || isValidGasStatus;
      })
      .map((expert) => ({
        firstname: expert.vorname,
        lastname: expert.nachname,
        isEnergyActive: expert.statusStrom === EIV_STATUS.ACTIVE,
        isGasActive: expert.statusGas === EIV_STATUS.ACTIVE,
        energyValidityDate:
          expert.statusStrom === EIV_STATUS.ACTIVE
            ? parseEIVDateStringToEfixDateString(
                expert.gueltigBisStrom || installer.auswStromGueltig || ''
              )
            : '',
        gasValidityDate:
          expert.statusGas === EIV_STATUS.ACTIVE
            ? parseEIVDateStringToEfixDateString(
                expert.gueltigBisGas || installer.auswGasGueltig || ''
              )
            : '',
      })),
  };

  return data;
};
